import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { filter, finalize } from 'rxjs/operators';
import { ContactUs } from 'src/app/shared/services/contact-us/contact-us';
import { ContactUsService } from 'src/app/shared/services/contact-us/contact-us.service';
import { ErrorComponent, InformationComponent } from 'src/app/shared/util/helpers';
import { SUBSCRIPTION_PRICES, SubscriptionPriceEnum } from 'src/app/shared/util/subscription-prices.enum';
import { TrackerService } from 'src/app/shared/util/tracker.service';
import { environment } from 'src/environments/environment';

const SCROLL_HEADER_OFFSET = 80;

export interface PricingPlanConfig {
  id: string;
  titleKey: string;
  badgeKey?: string;
  descKey: string;
  subscriptionKey?: string;
  priceLabelKey: string;
  staticPriceKey?: string;
  featureKeys: string[];
  noteKeys: string[];
  ctaKey: string;
  registerCta?: boolean;
  contactCta?: boolean;
  featured?: boolean;
  dark?: boolean;
}

export interface PricingCompareRow {
  labelKey: string;
  valueKeys: (string | null)[];
}

@Component({
  selector: 'app-pricing',
  templateUrl: './pricing.component.html',
  styleUrls: ['./pricing.component.scss'],
})
export class PricingComponent implements OnInit, AfterViewInit, OnDestroy {
  public contactItem: ContactUs = new ContactUs();
  public assistantHref = environment.assistantApp;

  subscriptionPrices = SUBSCRIPTION_PRICES;
  isLoading = false;
  openFaqIndex: number | null = 0;
  showCompareTable = false;

  private routerEventsSub: Subscription | null = null;
  private fragmentScrollTimer: ReturnType<typeof setTimeout> | null = null;

  readonly pricingFaqItems = [
    { questionKey: 'PRICING.QUESTION1', answerKey: 'PRICING.Q_ANSWER1' },
    { questionKey: 'PRICING.QUESTION2', answerKey: 'PRICING.Q_ANSWER2' },
  ];

  readonly plans: PricingPlanConfig[] = [
    {
      id: 'start',
      titleKey: 'PRICING.START_PLAN_TITLE',
      badgeKey: 'PRICING.START_TEXT',
      descKey: 'PRICING.START_FUNCTIONALITY_TEXT',
      subscriptionKey: 'start_subscription_monthly',
      priceLabelKey: 'PRICING.START_PRICE',
      featureKeys: [
        'PRICING.START_ITEM1_TEXT',
        'PRICING.START_ITEM2_TEXT',
        'PRICING.START_ITEM3_TEXT',
        'PRICING.START_ITEM4_TEXT',
        'PRICING.START_ITEM5_TEXT',
        'PRICING.START_ITEM6_TEXT',
      ],
      noteKeys: ['PRICING.START_ITEM_NOTES_1'],
      ctaKey: 'PRICING.PLAN_BUTTON_START_HERE',
      registerCta: true,
    },
    {
      id: 'smart',
      titleKey: 'PRICING.SMART_PLAN_TITLE',
      badgeKey: 'PRICING.SMART_PLAN_TEXT',
      descKey: 'PRICING.SMART_PLAN_FUNCTIONALITY_TEXT',
      subscriptionKey: 'smart_subscription_monthly',
      priceLabelKey: 'PRICING.SMART_PRICE',
      featureKeys: [
        'PRICING.SMART_ITEM1_TEXT',
        'PRICING.SMART_ITEM2_TEXT',
        'PRICING.SMART_ITEM3_TEXT',
        'PRICING.SMART_ITEM4_TEXT',
        'PRICING.SMART_ITEM5_TEXT',
        'PRICING.SMART_ITEM6_TEXT',
      ],
      noteKeys: ['PRICING.SMART_ITEM_NOTES_1'],
      ctaKey: 'PRICING.PLAN_BUTTON_START_HERE',
      registerCta: true,
      featured: true,
    },
    {
      id: 'scale',
      titleKey: 'PRICING.SCALE_PLAN_TITLE',
      badgeKey: 'PRICING.SCALE_PLAN_TEXT',
      descKey: 'HOME.SCALE_FUNCTIONALITY_TEXT',
      subscriptionKey: 'scale_subscription_monthly',
      priceLabelKey: 'PRICING.SCALE_PRICE',
      featureKeys: [
        'PRICING.SCALE_ITEM1_TEXT',
        'PRICING.SCALE_ITEM2_TEXT',
        'PRICING.SCALE_ITEM3_TEXT',
        'PRICING.SCALE_ITEM4_TEXT',
        'PRICING.SCALE_ITEM5_TEXT',
        'PRICING.SCALE_ITEM6_TEXT',
      ],
      noteKeys: ['PRICING.SCALE_ITEM_NOTES_1', 'PRICING.SCALE_ITEM_NOTES_2'],
      ctaKey: 'PRICING.PLAN_BUTTON_START_HERE',
      registerCta: true,
    },
    {
      id: 'propero',
      titleKey: 'PRICING.MANAGEMENT_PROPERO_TITLE',
      badgeKey: 'PRICING.MANAGEMENT_PROPERO_TEXT',
      descKey: 'PRICING.MANAGEMENT_PROPERO_FUNCTIONALITY_TEXT',
      staticPriceKey: 'PRICING.MANAGEMENT_PRICE',
      priceLabelKey: 'PRICING.START_PRICE',
      featureKeys: [
        'PRICING.MANAGEMENT_PROPERO_FUNCTIONALITY_1_TEXT',
        'PRICING.MANAGEMENT_PROPERO_FUNCTIONALITY_2_TEXT',
        'PRICING.MANAGEMENT_PROPERO_FUNCTIONALITY_3_TEXT',
        'PRICING.MANAGEMENT_PROPERO_FUNCTIONALITY_4_TEXT',
        'PRICING.MANAGEMENT_PROPERO_FUNCTIONALITY_5_TEXT',
        'PRICING.MANAGEMENT_PROPERO_FUNCTIONALITY_6_TEXT',
      ],
      noteKeys: ['PRICING.MANAGEMENT_PROPERO_ITEM_NOTES_1'],
      ctaKey: 'PRICING.REQUEST_OFFER_BUTTON',
      contactCta: true,
      dark: true,
    },
  ];

  readonly compareRows: PricingCompareRow[] = [
    {
      labelKey: 'PRICING.ITEM1_TITLE',
      valueKeys: [
        'PRICING.ITEM1_START_TEXT',
        'PRICING.ITEM1_SMART_TEXT',
        'PRICING.ITEM1_SCALE_TEXT',
        'PRICING.FUNCTIONALITY_OPTION_UNLIMITED',
      ],
    },
    {
      labelKey: 'PRICING.ITEM2_TITLE',
      valueKeys: [
        'PRICING.ITEM2_START_TEXT',
        'PRICING.ITEM2_SMART_TEXT',
        'PRICING.ITEM2_SCALE_TEXT',
        'PRICING.FUNCTIONALITY_OPTION_UNLIMITED',
      ],
    },
    {
      labelKey: 'PRICING.ITEM3_TITLE',
      valueKeys: [
        'PRICING.ITEM3_START_TEXT',
        'PRICING.ITEM3_SMART_TEXT',
        'PRICING.ITEM3_SCALE_TEXT',
        'PRICING.FUNCTIONALITY_OPTION_TOP',
      ],
    },
    {
      labelKey: 'PRICING.PLAN_ITEM4_TEXT',
      valueKeys: [
        'PRICING.FUNCTIONALITY_OPTION_BASIC',
        'PRICING.FUNCTIONALITY_OPTION_ADVANCED',
        'PRICING.FUNCTIONALITY_OPTION_PREMIUM',
        'PRICING.FUNCTIONALITY_OPTION_ENTERPRISE',
      ],
    },
    {
      labelKey: 'PRICING.PLAN_ITEM6_TEXT',
      valueKeys: [
        'PRICING.FUNCTIONALITY_OPTION_BASIC',
        'PRICING.FUNCTIONALITY_OPTION_ADVANCED',
        'PRICING.FUNCTIONALITY_OPTION_PREMIUM',
        'PRICING.FUNCTIONALITY_OPTION_ENTERPRISE',
      ],
    },
    {
      labelKey: 'PRICING.PLAN_ITEM7_TEXT',
      valueKeys: [
        'PRICING.FUNCTIONALITY_OPTION_BASIC',
        'PRICING.FUNCTIONALITY_OPTION_ADVANCED',
        'PRICING.FUNCTIONALITY_OPTION_PREMIUM',
        'PRICING.FUNCTIONALITY_OPTION_ENTERPRISE',
      ],
    },
    {
      labelKey: 'PRICING.ITEM12_TITLE',
      valueKeys: [
        'PRICING.ITEM12_START_TEXT',
        'PRICING.FUNCTIONALITY_OPTION_STANDARD',
        'PRICING.FUNCTIONALITY_OPTION_PREMIUM',
        'PRICING.FUNCTIONALITY_OPTION_UNLIMITED',
      ],
    },
    {
      labelKey: 'PRICING.PLAN_ITEM13_TEXT',
      valueKeys: [
        'PRICING.FUNCTIONALITY_OPTION_BASIC',
        'PRICING.FUNCTIONALITY_OPTION_ADVANCED',
        'PRICING.FUNCTIONALITY_OPTION_PREMIUM',
        'PRICING.FUNCTIONALITY_OPTION_ENTERPRISE',
      ],
    },
    {
      labelKey: 'PRICING.PLAN_ITEM14_TEXT',
      valueKeys: [
        null,
        'PRICING.FUNCTIONALITY_OPTION_ADVANCED',
        'PRICING.FUNCTIONALITY_OPTION_PREMIUM',
        'PRICING.FUNCTIONALITY_OPTION_ENTERPRISE',
      ],
    },
    {
      labelKey: 'PRICING.PLAN_ITEM22_TEXT',
      valueKeys: [
        null,
        'PRICING.FUNCTIONALITY_OPTION_BASIC',
        'PRICING.FUNCTIONALITY_OPTION_PREMIUM',
        'PRICING.FUNCTIONALITY_OPTION_ENTERPRISE',
      ],
    },
    {
      labelKey: 'PRICING.PLAN_ITEM27_TEXT',
      valueKeys: [
        'PRICING.FUNCTIONALITY_OPTION_BASIC',
        'PRICING.FUNCTIONALITY_OPTION_BASIC',
        'PRICING.FUNCTIONALITY_OPTION_PREMIUM',
        'PRICING.FUNCTIONALITY_OPTION_ENTERPRISE',
      ],
    },
    {
      labelKey: 'PRICING.PLAN_ITEM32_TEXT',
      valueKeys: [null, null, null, 'PRICING.MANAGEMENT_PROPERO_TITLE'],
    },
  ];

  constructor(
    public snackBar: MatSnackBar,
    public translate: TranslateService,
    private trackerService: TrackerService,
    public contactUsService: ContactUsService,
    private titleService: Title,
    private router: Router
  ) {
    this.titleService.setTitle('Propero - Pricing');
  }

  getSubscriptionPriceDetails(uniqueName: string) {
    const documentKey = Object.keys(this.subscriptionPrices).find((key) =>
      key.toLowerCase().includes(uniqueName.toLowerCase())
    );
    return documentKey ? this.subscriptionPrices[documentKey as SubscriptionPriceEnum] : null;
  }

  ngOnInit(): void {
    this.trackerService.trackPageViews();

    this.routerEventsSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        const fragment = this.getUrlFragment();
        if (fragment) {
          this.scheduleScrollToFragment(fragment);
        } else {
          window.scrollTo(0, 0);
        }
      });

    const initialFragment = this.getUrlFragment();
    if (initialFragment) {
      this.scheduleScrollToFragment(initialFragment);
    } else {
      window.scrollTo(0, 0);
    }
  }

  ngAfterViewInit(): void {
    const fragment = this.getUrlFragment();
    if (fragment) {
      this.scheduleScrollToFragment(fragment);
    }
  }

  ngOnDestroy(): void {
    this.routerEventsSub?.unsubscribe();
    if (this.fragmentScrollTimer) {
      clearTimeout(this.fragmentScrollTimer);
    }
  }

  toggleCompareTable(): void {
    this.showCompareTable = !this.showCompareTable;
  }

  toggleFaq(index: number): void {
    this.openFaqIndex = this.openFaqIndex === index ? null : index;
  }

  planCtaHref(plan: PricingPlanConfig): string {
    return `${this.assistantHref}/register`;
  }

  private getUrlFragment(): string | null {
    return this.router.parseUrl(this.router.url).fragment;
  }

  private scheduleScrollToFragment(fragment: string): void {
    if (this.fragmentScrollTimer) {
      clearTimeout(this.fragmentScrollTimer);
    }
    this.fragmentScrollTimer = setTimeout(() => this.scrollToFragment(fragment, 0), 50);
  }

  private scrollToFragment(fragment: string, attempt: number): void {
    const target = document.getElementById(fragment);
    if (target) {
      const top = target.getBoundingClientRect().top + window.pageYOffset - SCROLL_HEADER_OFFSET;
      window.scrollTo({
        top: Math.max(0, top),
        behavior: attempt === 0 ? 'smooth' : 'auto',
      });
      return;
    }

    if (attempt < 20) {
      setTimeout(() => this.scrollToFragment(fragment, attempt + 1), 100);
    }
  }

  send(): void {
    if (!this.contactItem.subject || !this.contactItem.email || !this.contactItem.content) {
      this.snackBar.openFromComponent(ErrorComponent, {
        data: { message: 'Vă rugăm să completați toate câmpurile.' },
        duration: 2000,
      });
      return;
    }

    this.isLoading = true;
    this.contactItem.date = new Date();

    this.contactUsService
      .addItem(this.contactItem)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        () => {
          this.contactItem.subject = '';
          this.contactItem.email = '';
          this.contactItem.content = '';
          this.snackBar.openFromComponent(InformationComponent, {
            data: { message: 'Mesajul a fost trimis cu succes!' },
            duration: 2000,
          });
        },
        () => {
          this.snackBar.openFromComponent(ErrorComponent, {
            data: { message: 'A apărut o eroare. Vă rugăm să încercați din nou.' },
            duration: 2000,
          });
        }
      );
  }
}
